import React, { useState, useEffect } from 'react';
import { Plus, AlertTriangle, CheckCircle, Pencil, Trash2, X, Save } from 'lucide-react';
import { hasPI } from '../../../lib/permissions';
import { piApi } from '../../../services/piApi';
import '../PIShell.css';

const LEVEL_LABELS  = { c_suite: 'C-Suite', vp: 'VP', director: 'Director' };
const STATUS_CONFIG = {
  active:   { label: 'Active',   cls: 'pi-badge-green' },
  at_risk:  { label: 'At Risk',  cls: 'pi-badge-red'   },
  vacant:   { label: 'Vacant',   cls: 'pi-badge-amber' },
  interim:  { label: 'Interim',  cls: 'pi-badge-blue'  },
};

const EMPTY_FORM = { full_name: '', role_title: '', level: 'c_suite', status: 'active', tenure_months: '', has_exit_experience: false, notes: '' };

export default function PITalentTab({ portco, currentUser }) {
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ ...EMPTY_FORM });
  const [editId, setEditId] = useState(null);
  const [editForm, setEditForm] = useState({ ...EMPTY_FORM });

  const canEdit = hasPI(currentUser, 'pi.talent.edit');

  const load = () => { piApi.getTalentProfiles(portco.id).then(r => setProfiles(r.data || [])).catch(() => {}).finally(() => setLoading(false)); };
  useEffect(() => { load(); }, [portco.id]);

  const handleAdd = async (e) => {
    e.preventDefault();
    await piApi.createTalentProfile(portco.id, { ...form, tenure_months: parseInt(form.tenure_months) || null }).catch(() => {});
    setForm({ ...EMPTY_FORM });
    setShowAdd(false);
    load();
  };

  const startEdit = (p) => {
    setEditId(p.id);
    setEditForm({
      full_name: p.full_name || '',
      role_title: p.role_title || '',
      level: p.level || 'c_suite',
      status: p.status || 'active',
      tenure_months: p.tenure_months != null ? String(p.tenure_months) : '',
      has_exit_experience: !!p.has_exit_experience,
      notes: p.notes || '',
    });
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    await piApi.updateTalentProfile(portco.id, editId, { ...editForm, tenure_months: parseInt(editForm.tenure_months) || null }).catch(() => {});
    setEditId(null);
    load();
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Remove this team member?')) return;
    await piApi.deleteTalentProfile(portco.id, id).catch(() => {});
    load();
  };

  const cSuiteWithoutExperience = profiles.filter(p => p.level === 'c_suite' && !p.has_exit_experience);
  const atRiskCount  = profiles.filter(p => p.status === 'at_risk').length;
  const vacantCount  = profiles.filter(p => p.status === 'vacant').length;
  const activeCount  = profiles.filter(p => p.status === 'active').length;

  const inputStyle = { padding: '6px 10px', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 13, width: '100%', boxSizing: 'border-box' };
  const cellStyle  = { padding: '10px 14px' };

  const renderFormRow = (f, setF, onSubmit, onCancel, submitLabel) => (
    <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
      <td style={cellStyle}>
        <input placeholder="Full name *" value={f.full_name} onChange={e => setF(v => ({ ...v, full_name: e.target.value }))} required style={inputStyle} />
      </td>
      <td style={cellStyle}>
        <input placeholder="Role title" value={f.role_title} onChange={e => setF(v => ({ ...v, role_title: e.target.value }))} style={inputStyle} />
      </td>
      <td style={cellStyle}>
        <select value={f.level} onChange={e => setF(v => ({ ...v, level: e.target.value }))} style={inputStyle}>
          <option value="c_suite">C-Suite</option>
          <option value="vp">VP</option>
          <option value="director">Director</option>
        </select>
      </td>
      <td style={cellStyle}>
        <select value={f.status} onChange={e => setF(v => ({ ...v, status: e.target.value }))} style={inputStyle}>
          <option value="active">Active</option>
          <option value="at_risk">At Risk</option>
          <option value="vacant">Vacant</option>
          <option value="interim">Interim</option>
        </select>
      </td>
      <td style={cellStyle}>
        <input type="number" placeholder="Months" value={f.tenure_months} onChange={e => setF(v => ({ ...v, tenure_months: e.target.value }))} style={inputStyle} />
      </td>
      <td style={cellStyle}>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, cursor: 'pointer', whiteSpace: 'nowrap' }}>
          <input type="checkbox" checked={f.has_exit_experience} onChange={e => setF(v => ({ ...v, has_exit_experience: e.target.checked }))} style={{ accentColor: '#4f46e5' }} />
          Yes
        </label>
      </td>
      <td style={cellStyle}>
        <input placeholder="Notes" value={f.notes} onChange={e => setF(v => ({ ...v, notes: e.target.value }))} style={inputStyle} />
      </td>
      <td style={{ ...cellStyle, whiteSpace: 'nowrap' }}>
        <div style={{ display: 'flex', gap: 4 }}>
          <button type="submit" onClick={onSubmit} className="pi-btn-primary" style={{ padding: '4px 10px', fontSize: 11 }}>
            <Save size={11} /> {submitLabel}
          </button>
          <button type="button" onClick={onCancel} className="pi-btn-ghost" style={{ padding: '4px 8px', fontSize: 11 }}>
            <X size={11} />
          </button>
        </div>
      </td>
    </tr>
  );

  return (
    <div style={{ padding: '24px 28px' }}>
      {/* Stat cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16, marginBottom: 20 }}>
        <div className="pi-stat-card"><div className="pi-stat-value">{activeCount}</div><div className="pi-stat-label">Active Executives</div></div>
        <div className="pi-stat-card"><div className="pi-stat-value" style={{ color: atRiskCount > 0 ? '#dc2626' : '#1e293b' }}>{atRiskCount}</div><div className="pi-stat-label">At-Risk Roles</div></div>
        <div className="pi-stat-card"><div className="pi-stat-value" style={{ color: vacantCount > 0 ? '#d97706' : '#1e293b' }}>{vacantCount}</div><div className="pi-stat-label">Vacant Roles</div></div>
      </div>

      {/* AI Warning banner */}
      {cSuiteWithoutExperience.length > 0 && (
        <div style={{ display: 'flex', gap: 10, background: '#fef3c7', border: '1px solid #fde68a', borderRadius: 8, padding: '12px 16px', marginBottom: 20, alignItems: 'flex-start' }}>
          <AlertTriangle size={18} style={{ color: '#d97706', flexShrink: 0, marginTop: 1 }} />
          <div>
            <div style={{ fontSize: 13, fontWeight: 600, color: '#92400e', marginBottom: 3 }}>
              {cSuiteWithoutExperience.length === 1 ? 'A C-suite member has' : `${cSuiteWithoutExperience.length} C-suite members have`} not previously managed a sale process.
            </div>
            <div style={{ fontSize: 12.5, color: '#92400e' }}>
              Consider engaging a transaction advisor or interim CFO 12–18 months before exit.
            </div>
          </div>
        </div>
      )}

      <div className="pi-card">
        <div className="pi-card-header">
          <span className="pi-card-title">Management Team</span>
          {canEdit && (
            <button className="pi-btn-ghost" style={{ fontSize: 11 }} onClick={() => { setShowAdd(s => !s); setEditId(null); }} aria-label="Add team member">
              <Plus size={12} /> Add Member
            </button>
          )}
        </div>

        <div style={{ overflowX: 'auto' }}>
          <form onSubmit={e => e.preventDefault()}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5 }}>
              <thead>
                <tr style={{ background: '#fafbff', borderBottom: '1px solid #f1f5f9' }}>
                  {['Name', 'Title', 'Level', 'Status', 'Tenure', 'Exit Exp.', 'Notes', ...(canEdit ? [''] : [])].map(h => (
                    <th key={h} style={{ padding: '8px 14px', textAlign: 'left', color: '#64748b', fontWeight: 500 }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {showAdd && canEdit && renderFormRow(form, setForm, handleAdd, () => setShowAdd(false), 'Add')}
                {loading ? (
                  <tr><td colSpan={canEdit ? 8 : 7}><div className="pi-loading" style={{ padding: 16 }}><div className="pi-spinner" /></div></td></tr>
                ) : profiles.length === 0 && !showAdd ? (
                  <tr><td colSpan={canEdit ? 8 : 7}>
                    <div className="pi-empty" style={{ padding: 24 }}>
                      <div className="pi-empty-title">No team members added yet</div>
                      {canEdit && <button className="pi-btn-ghost" onClick={() => setShowAdd(true)}><Plus size={13} /> Add first member</button>}
                    </div>
                  </td></tr>
                ) : profiles.map(p => {
                  if (editId === p.id && canEdit) {
                    return <React.Fragment key={p.id}>{renderFormRow(editForm, setEditForm, handleUpdate, () => setEditId(null), 'Save')}</React.Fragment>;
                  }
                  const cfg = STATUS_CONFIG[p.status] || STATUS_CONFIG.active;
                  const tenureStr = p.tenure_months != null
                    ? p.tenure_months >= 12 ? `${Math.floor(p.tenure_months / 12)}y ${p.tenure_months % 12}m` : `${p.tenure_months}m`
                    : '—';
                  return (
                    <tr key={p.id} style={{ borderBottom: '1px solid #f8fafc' }}>
                      <td style={{ ...cellStyle, fontWeight: 600, color: '#1e293b' }}>{p.full_name}</td>
                      <td style={{ ...cellStyle, color: '#475569' }}>{p.role_title || '—'}</td>
                      <td style={cellStyle}>
                        <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 8, background: '#f1f5f9', color: '#475569' }}>{LEVEL_LABELS[p.level] || p.level}</span>
                      </td>
                      <td style={cellStyle}>
                        <span className={`pi-badge ${cfg.cls}`} aria-label={`Status: ${cfg.label}`}>{cfg.label}</span>
                      </td>
                      <td style={{ ...cellStyle, color: '#64748b' }}>{tenureStr}</td>
                      <td style={cellStyle}>
                        {p.has_exit_experience
                          ? <CheckCircle size={15} style={{ color: '#16a34a' }} aria-label="Has exit experience" />
                          : <span style={{ color: p.level === 'c_suite' ? '#dc2626' : '#94a3b8', fontSize: 12 }} aria-label="No exit experience">
                              {p.level === 'c_suite' ? '⚠ No' : 'No'}
                            </span>
                        }
                      </td>
                      <td style={{ ...cellStyle, color: '#64748b', fontSize: 12, maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.notes || '—'}</td>
                      {canEdit && (
                        <td style={{ ...cellStyle, whiteSpace: 'nowrap' }}>
                          <div style={{ display: 'flex', gap: 4 }}>
                            <button type="button" className="pi-btn-ghost" style={{ padding: '4px 6px', fontSize: 11 }} onClick={() => { startEdit(p); setShowAdd(false); }} title="Edit">
                              <Pencil size={13} />
                            </button>
                            <button type="button" className="pi-btn-ghost" style={{ padding: '4px 6px', fontSize: 11, color: '#dc2626' }} onClick={() => handleDelete(p.id)} title="Delete">
                              <Trash2 size={13} />
                            </button>
                          </div>
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </form>
        </div>
      </div>
    </div>
  );
}
